import React, { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { CalendarIcon, MapPinIcon, PhoneIcon } from '@heroicons/react/24/solid';

const Accommodation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [pendingRoom, setPendingRoom] = useState(null);

  const handleBookClick = (roomId) => {
    setPendingRoom(roomId);
    setShowPrivacy(true);
  };

  const acceptPrivacyAndProceed = () => {
    setShowPrivacy(false);
    if (pendingRoom) {
      navigate(`/booking?room=${pendingRoom}`);
      setPendingRoom(null);
    }
  };

  useEffect(() => {
    // If navigated with a hash (for example from Navbar or another page), smooth-scroll to the target
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        // small timeout to allow layout to settle (images/loading)
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    }
  }, [location]);

  return (
    <div className="relative">
      {/* HERO section uses accommodation-bg.png */}
      <section className="relative h-screen min-h-[500px]">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/accommodation-bg.png)" }}
          role="img"
          aria-label="Casa Gatchalian accommodation background"
        />
        <div className="absolute inset-0 bg-black/60" aria-hidden="true" />

        {/* content: vertically centered, left padded */}
        <div className="absolute inset-0 flex items-center">
          <div className="px-4 sm:px-8 md:px-12 lg:pl-24 max-w-3xl text-white">
            <h2 className="font-bebas text-4xl sm:text-5xl md:text-6xl lg:text-[6rem] leading-[0.9] tracking-tightest mb-4 sm:mb-6">ACCOMODATION</h2>
            <p className="mb-4 sm:mb-6 text-white/90 leading-relaxed font-montserrat text-sm md:text-base max-w-md">
              Are you looking for affordable Vintage Venue with Catering Services for your intimate event? Casa Gatchalian Restobar is here for you. We will serve you with satisfaction on our ultimate dishes and venue styling.
            </p>

            <a
              href="#our-rooms"
              className="inline-block bg-transparent border border-[#caa89d] text-[#f6e9e3] px-5 sm:px-6 py-2.5 sm:py-3 uppercase tracking-widest text-[10px] sm:text-xs font-montserrat hover:bg-[#caa89d] hover:text-black transition-colors duration-300"
            >
              BOOK A ROOM NOW!
            </a>
          </div>
        </div>  
      </section>
      {/* Privacy modal shown before navigating to booking */}
      {showPrivacy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[#f4e7e1] max-w-2xl w-full rounded shadow-lg overflow-hidden">
            <div className="bg-[#5d4134] px-4 sm:px-6 py-3 sm:py-4 text-[#f4e7e1] font-semibold font-montserrat text-sm sm:text-base">Privacy Statement</div>
            <div className="p-4 sm:p-6 text-xs sm:text-sm text-gray-800">
              <p className="mb-3 sm:mb-4">This privacy policy sets out how we use and protects any information that you give us when you use this website. We are committed to ensuring that your privacy is protected.</p>
              <p className="mb-3 sm:mb-4">By clicking "Got It" you agree to our privacy practice and can proceed to complete the booking.</p>
            </div>
            <div className="p-3 sm:p-4 flex justify-end space-x-2 sm:space-x-3">
              <button onClick={() => setShowPrivacy(false)} className="px-3 sm:px-4 py-2 border border-[#5d4134] text-[#5d4134] rounded hover:bg-[#5d4134] hover:text-[#f4e7e1] transition-colors text-xs sm:text-sm">Cancel</button>
              <button onClick={acceptPrivacyAndProceed} className="px-3 sm:px-4 py-2 bg-[#5d4134] text-[#f4e7e1] rounded hover:bg-[#4b3029] transition-colors text-xs sm:text-sm">Got It</button>
            </div>
          </div>
        </div>
      )}

      {/* brown info strip sits between hero and rooms */}
      <div className="relative z-30 bg-[#5d4134] text-[#f4e7e1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#4b3029] flex items-center justify-center flex-shrink-0">
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#f4e7e1]" aria-hidden="true" />
            </div>
            <div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest font-montserrat">Opening Times</div>
              <div className="text-xs sm:text-sm">Mon-Fri 8:00 AM - 8:00 PM</div>
            </div>
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4 md:justify-center">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#4b3029] flex items-center justify-center flex-shrink-0">
              <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#f4e7e1]" aria-hidden="true" />
            </div>
            <div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest font-montserrat">Our Location</div>
              <div className="text-xs sm:text-sm">Batangas, San Luis, Philippines</div>
            </div>
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4 md:justify-end">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#4b3029] flex items-center justify-center flex-shrink-0">
              <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#f4e7e1]" aria-hidden="true" />
            </div>
            <div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest font-montserrat">Customer Support</div>
              <div className="text-xs sm:text-sm">0912-345-678 / 0917-765-432</div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Rooms section uses our-room-bg.png as its background */}
      <section id="our-rooms" className="relative py-12 sm:py-16 md:py-20">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/our-room-bg.png)" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/55" aria-hidden="true" />

        <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6">
          <h3 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bebas text-center text-[#ffffff] mb-6 sm:mb-8 tracking-tightest">OUR ROOMS</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="relative rounded-md overflow-hidden">
              <img src="/images/room-1.png" alt="Room 1" className="w-full h-64 sm:h-72 md:h-80 lg:h-96 object-cover block" />
              <div className="absolute left-0 right-0 bottom-0 bg-black/75 text-white px-4 sm:px-6 py-4 sm:py-6 border-t border-white/10">
                <h4 className="font-bebas text-xl sm:text-2xl mb-2">Room 1</h4>
                <p className="text-xs sm:text-sm text-gray-100 mb-3 sm:mb-4">Your Cozy Retreat. A comfortable and quiet space designed for restful nights. Perfect for solo travelers or a pair seeking simple, convenient lodging.</p>
                <div>
                  <button onClick={() => handleBookClick(1)} className="block w-full sm:w-3/4 mx-auto text-center py-2.5 sm:py-3 border border-white/60 text-white text-xs sm:text-sm hover:bg-white hover:text-black transition-colors">Book Now</button>
                </div>
              </div>
            </div>

            <div className="relative rounded-md overflow-hidden">
              <img src="/images/room-2.png" alt="Room 2" className="w-full h-64 sm:h-72 md:h-80 lg:h-96 object-cover block" />
              <div className="absolute left-0 right-0 bottom-0 bg-black/75 text-white px-4 sm:px-6 py-4 sm:py-6 border-t border-white/10">
                <h4 className="font-bebas text-xl sm:text-2xl mb-2">Room 2</h4>
                <p className="text-xs sm:text-sm text-gray-100 mb-3 sm:mb-4">Spacious & Accessible. Enjoy a larger floor plan with all the essentials you need. A great choice for added comfort or small groups needing more room to settle in.</p>
                <div>
                  <button onClick={() => handleBookClick(2)} className="block w-full sm:w-3/4 mx-auto text-center py-2.5 sm:py-3 border border-white/60 text-white text-xs sm:text-sm hover:bg-white hover:text-black transition-colors">Book Now</button>
                </div>
              </div>
            </div>

            <div className="relative rounded-md overflow-hidden">
              <img src="/images/room-3.png" alt="Room 3" className="w-full h-64 sm:h-72 md:h-80 lg:h-96 object-cover block" />
              <div className="absolute left-0 right-0 bottom-0 bg-black/75 text-white px-4 sm:px-6 py-4 sm:py-6 border-t border-white/10">
                <h4 className="font-bebas text-xl sm:text-2xl mb-2">Room 3</h4>
                <p className="text-xs sm:text-sm text-gray-100 mb-3 sm:mb-4">Essential Comfort. Our most convenient room, featuring reliable air-conditioning and a media setup for relaxation after a long day. Simple, clean, and functional.</p>
                <div>
                  <button onClick={() => handleBookClick(3)} className="block w-full sm:w-3/4 mx-auto text-center py-2.5 sm:py-3 border border-white/60 text-white text-xs sm:text-sm hover:bg-white hover:text-black transition-colors">Book Now</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events / Catering section (brown heading + dark gallery to match site colors) */}
      <section id="events" className="relative bg-[#141517]">
        {/* brown heading strip */}
        <div className="bg-[#5d4134] text-[#ffffff] py-4 sm:py-5 md:py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-montserrat text-center mb-0 tracking-widest">WE CATER AND ACCEPT EVENTS</h3>
          </div>
        </div>

        {/* dark gallery area (full-width section background) */}
        <div className="text-white py-6 sm:py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <p className="text-center text-gray-300 max-w-3xl mx-auto mb-6 sm:mb-8 text-sm sm:text-base">We provide full service catering and can host events at our venue. Here are some photos from recent events.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div className="group overflow-hidden rounded-md shadow-lg bg-[#0f1112]">
                <img src="/images/cater-1.jpg" alt="cater 1" className="w-full h-56 sm:h-64 md:h-72 object-cover block transform-gpu transition-transform duration-300 ease-out group-hover:scale-105 group-hover:-translate-y-1" />
              </div>
              <div className="group overflow-hidden rounded-md shadow-lg bg-[#0f1112]">
                <img src="/images/cater2.jpg" alt="cater 2" className="w-full h-56 sm:h-64 md:h-72 object-cover block transform-gpu transition-transform duration-300 ease-out group-hover:scale-105 group-hover:-translate-y-1" />
              </div>
              <div className="group overflow-hidden rounded-md shadow-lg bg-[#0f1112]">
                <img src="/images/cater3.jpg" alt="cater 3" className="w-full h-56 sm:h-64 md:h-72 object-cover block transform-gpu transition-transform duration-300 ease-out group-hover:scale-105 group-hover:-translate-y-1" />
              </div>
              <div className="group overflow-hidden rounded-md shadow-lg bg-[#0f1112]">
                <img src="/images/cater4.jpg" alt="cater 4" className="w-full h-56 sm:h-64 md:h-72 object-cover block transform-gpu transition-transform duration-300 ease-out group-hover:scale-105 group-hover:-translate-y-1" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catering Services and Event Booking Hero Section */}
      <section id="catering-hero" className="relative h-screen min-h-[500px] bg-cover bg-center flex items-center justify-start" style={{ backgroundImage: "url('/images/cater-events-bg.png')" }}>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16 md:py-20 w-full">
          <div className="max-w-2xl text-white">
            <h2 className="font-bebas text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-4 sm:mb-6 leading-tight">CATERING SERVICES </h2>
            <p className="text-sm sm:text-base md:text-[16px] leading-relaxed mb-6 sm:mb-8 text-gray-100">
              The perfect backdrop for your unforgettable event. Book our versatile venue for weddings, parties, or corporate functions. Our tailored catering services ensure your guests enjoy delicious, high-quality cuisine in a setting that's built for joy.
            </p>
            <Link to="/event-booking" className="inline-block bg-[#c9b8a8] text-gray-900 px-6 sm:px-8 py-2.5 sm:py-3 rounded hover:bg-[#b8a898] transition-colors font-montserrat tracking-widest text-xs sm:text-sm font-semibold">
              BOOK AN EVENT
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Accommodation;

